import { computed, ref, watch, type ComputedRef } from 'vue'

import { REFRESH_ALL_KEY } from './useVersionGate'

import {
  useAccountSession,
  type AccountBootstrapHooks,
  type DegradedSection,
  type RetryDegradedOutcome,
  type SessionStatus,
} from '@/features/account'
import { AuthLogoutFlow, useSession } from '@/features/auth'
import { primeReportCatalogs } from '@/features/reports'
import type { ApiError } from '@/shared/http'

export interface UseBootstrap {
  status: ComputedRef<SessionStatus>
  bootError: ComputedRef<ApiError | null>
  isDegraded: ComputedRef<boolean>
  degradedSections: ComputedRef<DegradedSection[]>
  /** True only after hydrated RBAC has revalidated the current route. */
  isRouteReady: ComputedRef<boolean>
  retry: () => Promise<void>
  /** Re-ask only for the retryable degraded sections (ORP-20409). Deliberately NOT routed through
   *  `run()`: that gate exists for a re-boot, and closing the route-render gate for a partial
   *  section refresh would blank the page the user is reading. `status` stays `ready` throughout. */
  retryDegradedSections: () => Promise<RetryDegradedOutcome>
}

export interface BootstrapCoordinatorOptions {
  navigate: (name: string) => void
  /** Re-check the already-resolved route after every successful boot/retry. */
  onReady?: () => void | Promise<void>
  /** Consumers for 07's post-boot hooks, supplied by the composition root (contract 07 §8).
   *  `onUnauthorized` is excluded on purpose — the shell wires that one itself, below, and a
   *  second producer would race the logout. */
  accountHooks?: Omit<AccountBootstrapHooks, 'onUnauthorized'>
  /** Eager-vs-deferred compositions seam, forwarded to the boot flow (07 §8). */
  isCompositionsRoute?: () => boolean
}

let installedCoordinator: UseBootstrap | null = null

/** Claim a pending `useVersionGate` reload, so it upgrades exactly one boot and is not replayed
 *  on the next one (v3 parity: `app/boot/bootUser.ts:20–23`). */
function claimRefreshAll(): boolean {
  if (localStorage.getItem(REFRESH_ALL_KEY) !== 'true') return false
  localStorage.setItem(REFRESH_ALL_KEY, 'false')
  return true
}

/**
 * The shell's one bootstrap trigger (contract 05 §8). Auth owns the bearer and account owns every
 * state transition; the shell only starts an idle session when a token exists and renders the
 * resulting idle/booting/error/ready state.
 */
export function createBootstrapCoordinator(options: BootstrapCoordinatorOptions): UseBootstrap {
  const auth = useSession()
  // Bootstrap requests suppress the global 401 handler (the flow owns its error taxonomy), so the
  // shell must wire the same logout target here — an unwired 401 would strand the session in
  // 'booting' behind the splash with no logout, no error surface, and no retry.
  const account = useAccountSession({
    hooks: {
      ...options.accountHooks,
      onUnauthorized: () => {
        void new AuthLogoutFlow({
          navigate: options.navigate,
        }).execute()
      },
    },
    ...(options.isCompositionsRoute ? { isCompositionsRoute: options.isCompositionsRoute } : {}),
  })

  // `status=ready` is not enough to paint a protected leaf: the current route must first be
  // evaluated against the just-hydrated RBAC. This prevents a one-frame denied-page mount.
  const routeReady = ref(account.status.value === 'ready')

  const run = async (operation: () => Promise<void>): Promise<void> => {
    routeReady.value = false
    try {
      await operation()
      if (account.status.value === 'ready') await options.onReady?.()
    } finally {
      // Error rendering is governed by account.status; ready rendering now has both gates.
      routeReady.value = true
    }
  }

  watch(
    auth.token,
    (token) => {
      if (!token) return
      // Global report catalogs are auth-gated but account-independent. One manifest primes both
      // definitions and visualization capabilities. DataVisible gates only chart rendering while
      // this settles; a catalog outage must not hold the whole authenticated app behind a splash.
      void primeReportCatalogs()
      if (account.status.value !== 'idle') return
      // AccountBootstrapFlow classifies failures into the session state machine; there is no
      // component-owned error state to duplicate here.
      void run(() => account.boot(claimRefreshAll()))
    },
    // A token write and its post-login navigation can happen in the same tick. Synchronous
    // observation installs the one-flight promise before permissionGuard reads the boot gate.
    { immediate: true, flush: 'sync' },
  )
  return {
    status: account.status,
    bootError: account.bootError,
    isDegraded: account.isDegraded,
    degradedSections: account.degradedSections,
    isRouteReady: computed(() => routeReady.value),
    retry: () => run(account.retry),
    retryDegradedSections: () => account.retryDegradedSections(),
  }
}

/**
 * Install the app-lifetime coordinator before Vue Router starts its first navigation. This keeps
 * the trigger shell-owned and single-flight while AppLayout renders its splash. It must run after
 * HTTP auth wiring and before `app.use(router)` so cold and post-login boots share one owner.
 */
export function installBootstrapCoordinator(options: BootstrapCoordinatorOptions): UseBootstrap {
  installedCoordinator ??= createBootstrapCoordinator(options)
  return installedCoordinator
}

/** AppLayout's read surface; installation belongs to the composition root. */
export function useBootstrap(): UseBootstrap {
  if (!installedCoordinator) {
    throw new Error('bootstrap coordinator not installed — call installBootstrapCoordinator first')
  }
  return installedCoordinator
}
